import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { persone, organizzazioni, tende } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Search, Plus, Edit, Trash2, QrCode, User, Phone, Mail, Calendar, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { openBadgePrintWindow, openBadgeBulkPrintWindow } from "@/components/badge/badgePrintUtils";

const INTOLLERANZE = [
  { id: "glutine", label: "Glutine" },
  { id: "lattosio", label: "Lattosio" },
  { id: "frutta_secca", label: "Frutta Secca" },
  { id: "uova", label: "Uova" },
  { id: "pesce", label: "Pesce" },
  { id: "crostacei", label: "Crostacei" },
];

const DOCUMENTI = [
  { value: "carta_identita", label: "Carta d'Identità" },
  { value: "patente", label: "Patente di Guida" },
  { value: "passaporto", label: "Passaporto" },
];

const initialFormData = {
  nome: "",
  cognome: "",
    qualifica: "",
  telefono: "",
  email: "",
  benefici_art_39: false,
  data_nascita: "",
  luogo_nascita: "",
  codice_fiscale: "",
  documento_tipo: "",
  documento_numero: "",
  documento_scadenza: "",
  intolleranze_alimentari: [],
  intolleranze_altro: "",
  note: "",
  organizzazioni: [],
  necessita_alloggio: false,
  tenda_assegnata_id: "",
};

export default function Anagrafica() {
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrPersona, setQrPersona] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOrgFilter, setBulkOrgFilter] = useState("");

  const queryClient = useQueryClient();

  const { data: personeData, isLoading } = useQuery({
    queryKey: ['persone', search],
    queryFn: () => persone.list({ search, limit: 100 }),
  });

  const { data: orgsData } = useQuery({
    queryKey: ['organizzazioni'],
    queryFn: organizzazioni.list,
  });

  const { data: tendeData } = useQuery({
    queryKey: ["tende"],
    queryFn: tende.list,
  });

  const createMutation = useMutation({
    mutationFn: persone.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['persone']);
      setDialogOpen(false);
      setFormData(initialFormData);
      toast.success("Persona creata con successo");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => persone.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['persone']);
      setDialogOpen(false);
      setSelectedPersona(null);
      setFormData(initialFormData);
      toast.success("Persona aggiornata con successo");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: persone.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['persone']);
      toast.success("Persona eliminata");
    },
    onError: (error) => toast.error(error.message),
  });

  const handleEdit = async (persona) => {
    try {
      const fullPersona = await persone.get(persona.id);
      setSelectedPersona(fullPersona);
      setFormData({
        nome: fullPersona.nome || "",
        cognome: fullPersona.cognome || "",
          qualifica: fullPersona.qualifica || "",
        telefono: fullPersona.telefono || "",
        email: fullPersona.email || "",
        benefici_art_39: fullPersona.benefici_art_39 || false,
        data_nascita: fullPersona.data_nascita || "",
        luogo_nascita: fullPersona.luogo_nascita || "",
        codice_fiscale: fullPersona.codice_fiscale || "",
        documento_tipo: fullPersona.documento_tipo || "",
        documento_numero: fullPersona.documento_numero || "",
        documento_scadenza: fullPersona.documento_scadenza || "",
        intolleranze_alimentari: fullPersona.intolleranze_alimentari || [],
        intolleranze_altro: fullPersona.intolleranze_altro || "",
        note: fullPersona.note || "",
        organizzazioni: fullPersona.organizzazioni?.map(o => o.id) || [],
      });
      setDialogOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nome?.trim() || !formData.cognome?.trim() || !formData.codice_fiscale?.trim()) { toast.error("Nome, Cognome e Codice Fiscale sono obbligatori"); return; }
    if (!formData.organizzazioni || formData.organizzazioni.length === 0) { toast.error("Seleziona almeno una organizzazione di appartenenza"); return; }
    if (selectedPersona) {
      updateMutation.mutate({ id: selectedPersona.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleIntolleranzaChange = (id, checked) => {
    setFormData(prev => ({
      ...prev,
      intolleranze_alimentari: checked
        ? [...prev.intolleranze_alimentari, id]
        : prev.intolleranze_alimentari.filter(i => i !== id)
    }));
  };

  const handleOrgChange = (orgId, checked) => {
    setFormData(prev => ({
      ...prev,
      organizzazioni: checked
        ? [...prev.organizzazioni, orgId]
        : prev.organizzazioni.filter(id => id !== orgId)
    }));
  };

  const showQrCode = (persona) => {
    setQrPersona(persona);
    setQrDialogOpen(true);
  };

  const getStatoBadgeColor = (stato) => {
    switch (stato) {
      case "consegnato": return "bg-green-100 text-green-700 border-green-200";
      case "non_consegnato": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "smarrito": return "bg-red-100 text-red-700 border-red-200";
      case "disabilitato": return "bg-slate-100 text-slate-700 border-slate-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="anagrafica-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Anagrafica</h1>
          <p className="text-slate-500 mt-1">Gestione persone nel campo</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setSelectedPersona(null);
            setFormData(initialFormData);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-persona-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Persona
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPersona ? "Modifica Persona" : "Nuova Persona"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dati Anagrafici */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" /> Dati Anagrafici
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      required
                      data-testid="input-nome"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      value={formData.cognome}
                      onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                      required
                      data-testid="input-cognome"
                    />
                  </div>
                </div>
            <div>
              <Label htmlFor="qualifica">Qualifica</Label>
              <Input
                id="qualifica"
                value={formData.qualifica}
                onChange={(e) => setFormData({ ...formData, qualifica: e.target.value })}
                data-testid="input-qualifica"
              />
            </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data_nascita">Data di Nascita</Label>
                    <Input
                      id="data_nascita"
                      type="date"
                      value={formData.data_nascita}
                      onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                      data-testid="input-data-nascita"
                    />
                  </div>
                  <div>
                    <Label htmlFor="luogo_nascita">Luogo di Nascita</Label>
                    <Input
                      id="luogo_nascita"
                      value={formData.luogo_nascita}
                      onChange={(e) => setFormData({ ...formData, luogo_nascita: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="codice_fiscale">Codice Fiscale *</Label>
                  <Input
                    id="codice_fiscale"
                    value={formData.codice_fiscale}
                    onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                    maxLength={16}
                    data-testid="input-codice-fiscale"
                  required
                  />
                </div>
              </div>

              {/* Contatti */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <Phone className="h-4 w-4" /> Contatti
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="telefono">Telefono</Label>
                    <Input
                      id="telefono"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      data-testid="input-telefono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="benefici_art_39"
                  className="h-4 w-4"
                  checked={!!formData.benefici_art_39}
                  onChange={(e) => setFormData({ ...formData, benefici_art_39: e.target.checked })}
                />
                <Label htmlFor="benefici_art_39">Richiede Benefici Art.39</Label>
              </div>
              </div>

              {/* Documento */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Documento
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Tipo Documento</Label>
                    <Select
                      value={formData.documento_tipo}
                      onValueChange={(value) => setFormData({ ...formData, documento_tipo: value })}
                    >
                      <SelectTrigger data-testid="select-documento-tipo">
                        <SelectValue placeholder="Seleziona..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENTI.map(doc => (
                          <SelectItem key={doc.value} value={doc.value}>{doc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="documento_numero">Numero</Label>
                    <Input
                      id="documento_numero"
                      value={formData.documento_numero}
                      onChange={(e) => setFormData({ ...formData, documento_numero: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="documento_scadenza">Scadenza</Label>
                    <Input
                      id="documento_scadenza"
                      type="date"
                      value={formData.documento_scadenza}
                      onChange={(e) => setFormData({ ...formData, documento_scadenza: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Intolleranze */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700">Intolleranze Alimentari</h3>
                <div className="grid grid-cols-3 gap-3">
                  {INTOLLERANZE.map(int => (
                    <div key={int.id} className="flex items-center gap-2">
                      <Checkbox
                        id={int.id}
                        checked={formData.intolleranze_alimentari.includes(int.id)}
                        onCheckedChange={(checked) => handleIntolleranzaChange(int.id, checked)}
                      />
                      <Label htmlFor={int.id} className="text-sm">{int.label}</Label>
                    </div>
                  ))}
                </div>
                <div>
                  <Label htmlFor="intolleranze_altro">Altre Intolleranze</Label>
                  <Input
                    id="intolleranze_altro"
                    value={formData.intolleranze_altro}
                    onChange={(e) => setFormData({ ...formData, intolleranze_altro: e.target.value })}
                    placeholder="Specificare..."
                  />
                </div>
              </div>

              {/* Organizzazioni */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700">Organizzazioni *</h3>
                <div className="grid grid-cols-2 gap-3 max-h-40 overflow-y-auto p-2 border rounded-md">
                  {orgsData?.map(org => (
                    <div key={org.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`org-${org.id}`}
                        checked={formData.organizzazioni.includes(org.id)}
                        onCheckedChange={(checked) => handleOrgChange(org.id, checked)}
                      />
                      <Label htmlFor={`org-${org.id}`} className="text-sm flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: org.colore_badge }}
                        />
                        {org.nome}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                />
              </div>


              {/* Sezione Alloggio */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-sm">Alloggio</h4>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="necessita_alloggio"
                    checked={formData.necessita_alloggio}
                    onCheckedChange={(checked) => setFormData({ ...formData, necessita_alloggio: checked })}
                  />
                  <Label htmlFor="necessita_alloggio">Necessita alloggio</Label>
                </div>
                {formData.necessita_alloggio && (
                  <div>
                    <Label htmlFor="tenda_assegnata_id">Tenda assegnata</Label>
                    <Select
                      value={formData.tenda_assegnata_id}
                      onValueChange={(value) => setFormData({ ...formData, tenda_assegnata_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona una tenda" />
                      </SelectTrigger>
                      <SelectContent>
                        {tendeData?.map((tenda) => (
                          <SelectItem key={tenda._id} value={tenda._id}>
                            {tenda.modello} - {tenda.posti_totali} posti
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#0B1E3D] hover:bg-[#1E40AF]"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="submit-persona-btn"
                >
                  {createMutation.isPending || updateMutation.isPending ? "Salvataggio..." : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Cerca per nome, cognome o codice QR..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Persone List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1E3D]"></div>
        </div>
      ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 mb-4 bg-slate-50 border border-slate-200 rounded-lg no-print" data-testid="bulk-print-toolbar">
                <div className="flex items-center gap-3 flex-wrap">
                  <Users className="h-4 w-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Stampa Badge Massiva (A4 = 4 badge A6)</span>
                  <Select value={bulkOrgFilter || "__all__"} onValueChange={(v) => setBulkOrgFilter(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="w-64"><SelectValue placeholder="Filtra per organizzazione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tutte le organizzazioni</SelectItem>
                      {(orgsData || []).map(o => (
                        <SelectItem key={o.id} value={String(o.id)}>{o.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    const list = (personeData || []).filter(p => !bulkOrgFilter || (p.organizzazioni || []).some(o => String(o.id) === bulkOrgFilter));
                    const all = new Set(selectedIds);
                    const allSelected = list.length > 0 && list.every(p => all.has(p.id));
                    if (allSelected) { list.forEach(p => all.delete(p.id)); } else { list.forEach(p => all.add(p.id)); }
                    setSelectedIds(all);
                  }}>Seleziona/Deseleziona visibili</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Pulisci</Button>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600"><b>{selectedIds.size}</b> selezionati</span>
                  <Button type="button" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" size="sm" disabled={selectedIds.size === 0} onClick={() => {
                    const map = new Map((personeData || []).map(p => [p.id, p]));
                    const list = Array.from(selectedIds).map(id => map.get(id)).filter(Boolean);
                    openBadgeBulkPrintWindow(list);
                  }} data-testid="bulk-print-btn">
                    <QrCode className="h-4 w-4 mr-2" /> Stampa Badge ({selectedIds.size})
                  </Button>
                </div>
              </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="persone-list">
          {personeData?.map(persona => (
            <Card key={persona.id} className="relative hover:shadow-md transition-shadow" data-testid={`persona-card-${persona.id}`}>
              <div className="absolute top-2 left-2 z-10 no-print">
                <Checkbox checked={selectedIds.has(persona.id)} onCheckedChange={(v) => { const next = new Set(selectedIds); if (v) next.add(persona.id); else next.delete(persona.id); setSelectedIds(next); }} data-testid={`bulk-check-${persona.id}`} />
              </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#0B1E3D] flex items-center justify-center text-white font-bold">
                      {persona.nome[0]}{persona.cognome[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{persona.nome} {persona.cognome}</h3>
                      <p className="text-xs text-slate-500 font-mono">{persona.qr_code}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => showQrCode(persona)}
                    data-testid={`qr-btn-${persona.id}`}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                </div>

                {/* Contact Info */}
                <div className="mt-4 space-y-1">
                  {persona.telefono && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Phone className="h-3 w-3" /> {persona.telefono}
                    </p>
                  )}
                  {persona.email && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Mail className="h-3 w-3" /> {persona.email}
                    </p>
                  )}
                </div>

                {/* Organizations */}
                {persona.organizzazioni?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {persona.organizzazioni.map(org => (
                      <Badge 
                        key={org.id} 
                        variant="outline" 
                        className="text-xs"
                        style={{ borderColor: org.colore_badge, color: org.colore_badge }}
                      >
                        {org.nome}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Badge Status & Actions */}
                <div className="mt-4 flex items-center justify-between">
                  <Badge className={getStatoBadgeColor(persona.stato_badge)}>
                    {persona.stato_badge?.replace("_", " ")}
                  </Badge>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleEdit(persona)}
                      data-testid={`edit-btn-${persona.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => {
                        if (window.confirm("Sei sicuro di voler eliminare questa persona?")) {
                          deleteMutation.mutate(persona.id);
                        }
                      }}
                      data-testid={`delete-btn-${persona.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {personeData?.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna persona trovata</p>
            </div>
          )}
        </div>
          </>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Badge QR Code</DialogTitle>
          </DialogHeader>
          {qrPersona && (
            <div className="text-center space-y-4">
              <div className="badge-card mx-auto max-w-xs">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                    {qrPersona.nome[0]}{qrPersona.cognome[0]}
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-lg">{qrPersona.nome} {qrPersona.cognome}</h3>
                    <p className="text-sm opacity-80">{qrPersona.organizzazioni?.[0]?.nome || "Volontario"}</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <QRCodeSVG
                    value={qrPersona.qr_code}
                    size={150}
                    level="H"
                    className="mx-auto"
                  />
                  <p className="mt-2 font-mono text-sm text-slate-900">{qrPersona.qr_code}</p>
                </div>
              </div>
              <Button onClick={() => openBadgePrintWindow(qrPersona)} className="bg-[#0B1E3D] hover:bg-[#1E40AF]">
                Stampa Badge
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
